import React, { useEffect, useState, useCallback } from "react";
import { Field, Form } from "react-final-form";
import { Box, Typography } from "@mui/material";
import { useAppDispatch } from "../../redux/hook";
import { facebookOrGoogleSignUp } from "../../redux/actions";
import {
  formatPhone,
  isObjectNotNull,
  isStringNotNull,
  renderErrorMessage,
} from "../../utils";
import { VALIDATION_MESSAGES } from "../../constants/ValidationMessage";
import Spinner from "../Spinner";
import { PRIMARY, UNIT } from "../../constants";
import { useSignInSignUp } from "../../hooks/useSignInSignUp";
import { validateFormValues } from "./SignupForm.services";
import _ from "lodash";
import {
  analyticsEvents,
  PAGE_TYPES,
  PAGES,
  PROVENANCE,
} from "../../utils/firebaseAnalytics";
import {
  Button,
  InputField,
  PhoneField,
  CustomInput,
} from "@dishopsaas/design-system";
import {
  SignupFormProps,
  SignupFormValues,
  RenderInputProps,
  RenderCheckboxProps,
} from "../../1_types/signUpTypes";
import CGU from "./components/CGU";
import styles from "./SignupForm.styles";

const PhoneInputField: React.FC<{
  input: any;
  meta: any;
  phone: string;
  setPhone: (value: string) => void;
  setIsPhoneIncorrect: (value: boolean) => void;
  setPhoneObj: (value: any) => void;
  phoneObj: any;
  isPhoneIncorrect: boolean;
  setPhoneError: (value: string) => void;
  phoneError: string;
  formSubmitted: boolean
}> = ({
  input,
  phone,
  setPhone,
  setIsPhoneIncorrect,
  setPhoneObj,
  phoneObj,
  isPhoneIncorrect,
  setPhoneError,
  phoneError,
}) => {
    return (
      <Box sx={{ mb: 2 }}>
        <PhoneField
          label="Téléphone"
          value={phone}
          onChange={(value) => {
            setPhone(value);
            input.onChange(value);
            setPhoneError(null)
          }}
          isPhoneIncorrect={setIsPhoneIncorrect}
          getPhoneObj={(obj: any) => {
            if (!_.isEqual(obj, phoneObj)) {
              setPhoneObj(obj);
            }
          }}
          error={isPhoneIncorrect}
          requiredMessage={phoneError}
        />
      </Box>
    );
  };


const FormInputField: React.FC<RenderInputProps> = ({
  input,
  meta,
  placeholder,
  description,
  email,
  infoText,
}) => {
  const isEmail = input.name === "email";
  const value = isEmail ? email : input.value;
  const type = input.type;

  return (
    <Box sx={{ mb: 2 }}>
      <InputField
        label={placeholder}
        type={type}
        value={value}
        onChange={input.onChange}
        onBlur={input.onBlur}
        onFocus={input.onFocus}
        disabled={isEmail}
        autoComplete={
          input.name === "email"
            ? "email"
            : input.name === "password"
              ? "new-password"
              : "off"
        }
        error={meta.touched && meta.error}
        helperText={meta.touched && meta.error}
        placeholder={placeholder}
        infoText={infoText}
      />
      {description && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={styles.descriptionText}
        >
          {description}
        </Typography>
      )}
    </Box>
  );
};

const FormCheckboxField: React.FC<RenderCheckboxProps> = ({
  input,
  meta,
  description,
  id,
  sx = {},
}) => (
  <Box sx={{ ...sx, mb: 1 }}>
    <CustomInput
      type="checkbox"
      label={<span style={{ marginLeft: UNIT * 0.625 }}>{description}</span>}
      fontSize="12px!important"
      checked={input.value || false}
      onChange={(checked: boolean) => input.onChange(checked)}
      name={id}
      isCustomInput
    />
    {meta.touched && meta.error && (
      <Typography
        color="error"
        variant="caption"
        sx={{ display: "block", mt: 0.5 }}
      >
        {meta.error}
      </Typography>
    )}
  </Box>
);

const SignupForm: React.FC<SignupFormProps> = ({ onSubmit, errorMessage }) => {
  const dispatch = useAppDispatch();
  const { name, email, signUpData, loadingConnexion, secondaryColor } =
    useSignInSignUp();

  const [phone, setPhone] = useState("");
  const [isPhoneIncorrect, setIsPhoneIncorrect] = useState(false);
  const [phoneObj, setPhoneObj] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [phoneError, setPhoneError] = useState("");
  useEffect(() => {
    analyticsEvents.logPageViewEvent(
      PAGES.SIGN_UP,
      PROVENANCE.LOGIN_PAGE,
      PAGE_TYPES.INTERNAL
    );
  }, []);
  useEffect(() => {
    if (!isObjectNotNull(phone) && formSubmitted) {
      setPhoneError(VALIDATION_MESSAGES.REQUIRED.PHONE);
    }
  }, [formSubmitted]);
  const handleSubmitSignup = useCallback(
    async (formValues: {
      password: string;
      confirmPassword: string;
      nom: string;
      prenom: string;
      phone: string;
      newsletter: boolean;
    }) => {
      setFormSubmitted(true);

      const { password, nom, prenom, phone, newsletter } = formValues;

      if (isPhoneIncorrect || !isStringNotNull(phone)) {

        return
      }

      const numero = formatPhone(phone);

      if (isObjectNotNull(signUpData)) {
        const { firstName, lastName, uid, loginMethod } = signUpData;
        const profile = {
          firstName,
          lastName,
          email: signUpData.email,
          id: uid,
          newsletter: newsletter || false,
          numero,
          phone: phoneObj,
        };
        dispatch(facebookOrGoogleSignUp(profile, loginMethod));
      } else {
        onSubmit(
          email,
          password,
          nom,
          prenom,
          numero,
          phoneObj,
          newsletter || false
        );
      }
    },
    [isPhoneIncorrect, signUpData, dispatch, phoneObj, email, onSubmit]
  );

  const validateForm = useCallback(
    (values: SignupFormValues) => {
      return validateFormValues({ ...values, email }, signUpData);
    },
    [email, signUpData, phoneError]
  );

  const renderFormContent = useCallback(
    ({ handleSubmit }: { handleSubmit: () => void }) => (
      <form onSubmit={handleSubmit} >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
          }}
        >
          {errorMessage && (
            <Typography variant="body1" sx={styles.errorText}>
              {renderErrorMessage(errorMessage)}
            </Typography>
          )}

          {!isObjectNotNull(signUpData) && (
            <>
              <Field
                name="email"
                type="text"
                placeholder="Adresse e-mail"
                render={(props) => <FormInputField {...props} email={email} />}
              />

              <Box sx={styles.fieldsContainer}>
                <Box sx={styles.fieldWrapper}>
                  <Field
                    name="prenom"
                    type="text"
                    placeholder="Prénom"
                    component={FormInputField}
                  />
                </Box>
                <Box sx={styles.fieldWrapper}>
                  <Field
                    name="nom"
                    type="text"
                    placeholder="Nom"
                    component={FormInputField}
                  />
                </Box>
              </Box>

              <Field
                name="password"
                type="password"
                placeholder="Mot de Passe"
                infoText="Minimum 8 caractères"
                component={FormInputField}
              />
              <Field
                name='confirmPassword'
                type='password'
                placeholder="Confirmation du mot de passe"
                infoText="Minimum 8 caractères"
                component={FormInputField}
              />
            </>
          )}

          <Field
            name="phone"
            type="text"
            render={(props) => (
              <PhoneInputField
                {...props}
                phone={phone}
                setPhone={setPhone}
                setIsPhoneIncorrect={setIsPhoneIncorrect}
                setPhoneObj={setPhoneObj}
                phoneObj={phoneObj}
                isPhoneIncorrect={isPhoneIncorrect}
                setPhoneError={setPhoneError}
                phoneError={phoneError}
                formSubmitted={formSubmitted}
              />
            )}
          />

          <Box sx={styles.checkboxContainer}>
            <Field
              name="newsletter"
              type="checkbox"
              render={(props) => (
                <FormCheckboxField
                  {...props}
                  description={
                    <Typography
                      variant="caption"
                      style={{ display: "inline-block" }}
                    >
                      Je souhaite recevoir les offres personnalisées et
                      promotions
                    </Typography>
                  }
                  id="newsletterCheckbox"
                />
              )}
            />

          </Box>

          {loadingConnexion ? (
            <Box sx={styles.spinnerContainer}>
              <Spinner />
            </Box>
          ) : (
            <>
              <Box sx={styles.buttonContainer}>
                <Button
                  type="submit"
                  colorType={PRIMARY}
                  sx={styles.button}
                  size="large"
                  width="100%"
                  onClick={() => setFormSubmitted(true)}
                >
                  CRÉER MON COMPTE
                </Button>
              </Box>

              <Box sx={styles.cguContainer}>
                <CGU />
              </Box>
            </>
          )}
        </Box>
      </form>
    ),
    [
      errorMessage,
      phoneError,
      signUpData,
      email,
      phone,
      phoneObj,
      isPhoneIncorrect,
      secondaryColor,
      name,
      loadingConnexion,
    ]
  );

  return (
    <Form<SignupFormValues>
      onSubmit={handleSubmitSignup}
      validate={validateForm}
      render={renderFormContent}
    />
  );
};

export default SignupForm;
